/**

 * Map a Fusion approval task (catalog row) to slices of bpmTenantConfig / approval-config.

 */



export function normalizeTaskMatchKey(name) {

  return String(name || '')

    .trim()

    .toLowerCase()

    .replace(/\s+/g, ' ');

}



/** Alphanumeric-only key — bridges "Global Absence Recording" vs GlobalAbsenceRecording. */

export function compactAlnum(s) {

  return String(s || '')

    .toLowerCase()

    .replace(/[^a-z0-9]/g, '');

}



function splitWords(s) {

  return String(s || '')

    .split(/[\s\-–_/]+/)

    .map((w) => w.trim())

    .filter((w) => w.length > 2);

}



/** Significant tokens from task name/code for loose matching. */

export function getTaskMatchTokens(task) {

  const tokens = new Set();

  for (const raw of [task?.name, task?.code, task?.product]) {

    if (!raw) continue;

    tokens.add(normalizeTaskMatchKey(raw));

    tokens.add(compactAlnum(raw));

    for (const w of splitWords(normalizeTaskMatchKey(raw))) {

      if (w.length > 2) tokens.add(w);

    }

  }

  if (task?.category === 'absence') {

    tokens.add('absence');

    tokens.add('absences');

    tokens.add('globalabsencerecording');

    tokens.add('absencerecording');

  }

  if (task?.category === 'time') {

    tokens.add('time');

    tokens.add('timecard');

    tokens.add('labor');

  }

  return [...tokens].filter(Boolean);

}



/** Fuzzy match between task catalog name/code and arbitrary tenant text fields. */

export function textMatchesTask(text, task, { loose = false } = {}) {

  if (text == null || text === '') return false;

  const hay = normalizeTaskMatchKey(text);

  const hayCompact = compactAlnum(text);

  if (!hay && !hayCompact) return false;

  if (!task?.name && !task?.code) return false;



  const name = normalizeTaskMatchKey(task.name);

  const code = task.code ? normalizeTaskMatchKey(task.code) : '';

  const nameCompact = compactAlnum(task.name);

  const codeCompact = task.code ? compactAlnum(task.code) : '';



  if (hay === name || (code && hay === code)) return true;

  if (nameCompact && hayCompact && (hayCompact === nameCompact || hayCompact.includes(nameCompact) || nameCompact.includes(hayCompact))) {

    const minLen = Math.min(hayCompact.length, nameCompact.length);

    if (minLen >= 6 || nameCompact.length >= 4) return true;

  }

  if (codeCompact && hayCompact && (hayCompact === codeCompact || hayCompact.includes(codeCompact) || codeCompact.includes(hayCompact))) {

    const minLen = Math.min(hayCompact.length, codeCompact.length);

    if (minLen >= 4) return true;

  }

  if (name && (hay.includes(name) || name.includes(hay))) return true;

  if (code && (hay.includes(code) || code.includes(hay))) return true;



  const wordsHay = splitWords(hay);

  const wordsName = splitWords(name);

  if (wordsHay.length && wordsName.length) {

    const overlap = wordsHay.filter((w) =>

      wordsName.some((wn) => wn.includes(w) || w.includes(wn))

    );

    const need = Math.min(2, Math.min(wordsHay.length, wordsName.length));

    if (overlap.length >= need) return true;

  }

  const absenceAnchor = 'globalabsencerecording';

  if (

    (nameCompact.includes(absenceAnchor) || nameCompact.includes('absencerecording')) &&

    (hayCompact.includes(absenceAnchor) || hayCompact.includes('absencerecording'))

  ) {

    return true;

  }

  if (loose && hayCompact) {

    const tokens = getTaskMatchTokens(task).map(compactAlnum).filter((t) => t.length >= 5);

    if (tokens.some((t) => hayCompact.includes(t) || t.includes(hayCompact))) return true;

  }



  return false;

}



function collectStrings(row, keys) {

  const out = [];

  for (const k of keys) {

    const v = row?.[k];

    if (v == null || v === '') continue;

    if (Array.isArray(v)) out.push(...v.map(String));

    else out.push(String(v));

  }

  return out;

}



function rowMatchesTask(row, fieldKeys, task, opts) {

  return collectStrings(row, fieldKeys).some((text) => textMatchesTask(text, task, opts));

}



function emptySlice(searched = {}) {

  return {

    rules: [],

    inferredRouting: [],

    bpmTasks: [],

    notifications: [],

    transactions: [],

    approvalGroups: [],

    hasMatch: false,

    matchKeys: [],

    matchTier: 'none',

    searched,

    suggestedDefinitions: [],

  };

}



/**

 * @param {object} tenantConfig — approval-config API payload

 * @param {object} task — catalog row { name, code, category, confirmedOnTenant, … }

 */

export function filterTenantConfigForTask(tenantConfig, task) {

  const searched = {

    taskName: task?.name || null,

    taskCode: task?.code || null,

    taskCategory: task?.category || null,

    confirmedOnTenant: !!task?.confirmedOnTenant,

  };

  if (!tenantConfig || (!task?.name && !task?.code)) {

    return emptySlice(searched);

  }



  const matchKeys = [];

  const loose = !!task.confirmedOnTenant;

  const opts = { loose };

  const match = (text, key) => {

    if (textMatchesTask(text, task, opts)) {

      if (key && !matchKeys.includes(key)) matchKeys.push(key);

      return true;

    }

    return false;

  };



  const rules = (tenantConfig.rules || []).filter((r) => {

    const ok =

      match(r.transactionType, 'rule.transactionType') ||

      match(r.ruleName, 'rule.ruleName') ||

      match(r.conditionText, 'rule.conditionText');

    return ok;

  });



  const inferredRouting = (tenantConfig.inferredRouting || []).filter((r) => {

    const fields = [

      r.taskDefinitionName,

      ...(r.sampleTitles || []),

      ...(r.workFlowPatterns || []),

      ...(r.approvers || []),

      ...(r.assignees || []),

    ];

    return fields.some((t) => match(t, 'inferred'));

  });



  const bpmTasks = (tenantConfig.bpmTasks || []).filter((t) =>

    rowMatchesTask(

      t,

      ['taskDefinitionName', 'title', 'identificationKey', 'assignees', 'ownerUser'],

      task,

      opts

    )

  );



  const notifications = (tenantConfig.notifications || []).filter((n) =>

    rowMatchesTask(

      n,

      ['taskDefinitionName', 'title', 'identificationKey', 'assignees', 'approvers', 'workFlowPattern'],

      task,

      opts

    )

  );



  const transactions = (tenantConfig.transactions || []).filter((tx) =>

    rowMatchesTask(tx, ['transactionName', 'processName', 'module', 'assignee'], task, opts)

  );



  const groupKeys = new Set();

  for (const r of rules) {

    for (const g of r.approvalGroupRefs || []) {

      groupKeys.add(normalizeTaskMatchKey(g));

    }

    for (const a of r.approvers || []) {

      if (a.ref) groupKeys.add(normalizeTaskMatchKey(a.ref));

    }

  }

  const approvalGroups = (tenantConfig.approvalGroups || []).filter((g) => {

    const nk = normalizeTaskMatchKey(g.name);

    const ck = normalizeTaskMatchKey(g.code);

    return (

      groupKeys.has(nk) ||

      groupKeys.has(ck) ||

      match(g.name, 'group.name') ||

      match(g.description, 'group.description') ||

      match(g.code, 'group.code')

    );

  });



  let matchTier = 'strict';

  let hasMatch =

    rules.length +

      inferredRouting.length +

      bpmTasks.length +

      notifications.length +

      transactions.length +

      approvalGroups.length >

    0;



  // Reference catalog tasks: surface category-related runtime rows when name match fails.

  if (!hasMatch && !task.confirmedOnTenant && task.category) {

    const cat = task.category;

    const catMatch = (text) => {

      const n = normalizeTaskMatchKey(text);

      if (cat === 'absence' && /\babsence|leave|pto|vacation|sick\b/.test(n)) return true;

      if (cat === 'time' && /\btime\s*card|timecard|time\s*entry|labor|hxt\b/.test(n)) return true;

      return false;

    };

    const inf2 = (tenantConfig.inferredRouting || []).filter((r) =>

      catMatch(r.taskDefinitionName) || (r.sampleTitles || []).some(catMatch)

    );

    if (inf2.length) {

      inferredRouting.push(...inf2.slice(0, 12));

      matchKeys.push('category:inferred');

      matchTier = 'category';

      hasMatch = true;

    }

  }



  const suggestedDefinitions = !hasMatch

    ? (tenantConfig.inferredRouting || [])

        .map((r) => r.taskDefinitionName)

        .filter(Boolean)

        .slice(0, 10)

    : [];



  return {

    rules,

    inferredRouting,

    bpmTasks,

    notifications,

    transactions,

    approvalGroups,

    hasMatch,

    matchKeys,

    matchTier,

    searched,

    suggestedDefinitions,

  };

}



function mapApproverKind(type) {

  const t = String(type || '').toLowerCase();

  if (/group|approval\s*group/.test(t)) return 'approvalGroup';

  if (/manager|hierarchy|supervisor/.test(t)) return 'lineManager';

  if (/auto/.test(t)) return 'autoApprove';

  return 'approvalGroup';

}



/** Seed editable design from tenant REST rules (when present). */

export function designFromTenantRules(rules, taskName) {

  const stages = [];

  for (const rule of rules || []) {

    for (const a of rule.approvers || []) {

      stages.push({

        order: Number(a.order) || stages.length + 1,

        routing: String(a.routing || 'sequential').toLowerCase().includes('parallel')

          ? 'parallel'

          : 'sequential',

        approvers: [{ kind: mapApproverKind(a.type), ref: a.ref || 'n/a' }],

      });

    }

  }

  const sorted = [...stages].sort((a, b) => (a.order || 0) - (b.order || 0));

  return {

    summary: sorted.length

      ? `Draft from ${sorted.length} tenant rule stage(s) for ${taskName}`

      : `Draft for ${taskName} — edit stages below`,

    transactionHint: taskName,

    stages:

      sorted.length > 0

        ? sorted

        : [

            {

              order: 1,

              routing: 'sequential',

              approvers: [{ kind: 'lineManager', ref: 'n/a' }],

            },

          ],

    conditions: [],

  };

}



/** Seed draft from inferred BPM routing when REST rules are unavailable. */

export function designFromInferredRouting(inferredRows, taskName) {

  const row = inferredRows?.[0];

  const stages = [];

  const patterns = row?.workFlowPatterns || [];

  const routing = patterns.some((p) => /parallel/i.test(p)) ? 'parallel' : 'sequential';

  const approverLabels = [...(row?.approvers || []), ...(row?.assignees || [])].filter(Boolean);

  if (approverLabels.length) {

    approverLabels.slice(0, 4).forEach((label, i) => {

      const kind = /manager|supervisor/i.test(label) ? 'lineManager' : 'approvalGroup';

      stages.push({

        order: i + 1,

        routing,

        approvers: [{ kind, ref: String(label).slice(0, 80) }],

      });

    });

  }

  return {

    summary: stages.length

      ? `Draft from tenant runtime routing (${row?.taskDefinitionName || taskName})`

      : `Draft for ${taskName} — edit stages below`,

    transactionHint: taskName,

    stages:

      stages.length > 0

        ? stages

        : [

            {

              order: 1,

              routing: 'sequential',

              approvers: [{ kind: 'lineManager', ref: 'n/a' }],

            },

          ],

    conditions: [],

  };

}



export function taskCategoryToTransactionCategory(category) {

  if (category === 'time') return 'time_labor';

  if (category === 'absence') return 'absence';

  return 'other';

}



export function slugTaskRuleId(taskName) {

  const base = normalizeTaskMatchKey(taskName)

    .replace(/[^a-z0-9]+/g, '-')

    .replace(/^-|-$/g, '')

    .slice(0, 48);

  return `task-${base || 'untitled'}`;

}


